import { distance, type Complex } from "./complex";
import { dft, selectCoefficients } from "./dft";
import { epicycles, sampleTrail, toSvgPath } from "./epicycles";
import { cubicPoint } from "./samplePath";

/** Arc-length samples of one authored cubic stroke (M, C and optional Z). */
export const sampleCubicStroke = (path: string, closed: boolean, count = 256): Complex[] => {
  const tokens = path.match(/[MCZ]|-?\d+(?:\.\d+)?/g) ?? [];
  const dense: Complex[] = [];
  let index = 0;
  let current: Complex = { x: 0, y: 0 };
  let start: Complex = current;
  const point = () => ({ x: Number(tokens[index++]), y: Number(tokens[index++]) });
  while (index < tokens.length) {
    const command = tokens[index++];
    if (command === "M") {
      current = point();
      start = current;
      dense.push(current);
    } else if (command === "C") {
      const curve = { from: current, c1: point(), c2: point(), to: point() };
      for (let step = 1; step <= 48; step++) dense.push(cubicPoint(curve, step / 48));
      current = curve.to;
    } else if (command === "Z" && closed) {
      if (distance(current, start) > 1e-8) dense.push(start);
    } else {
      throw new Error(`Unsupported Fourier stroke command: ${command}`);
    }
  }
  if (dense.length < 2) throw new Error("Fourier stroke needs at least two points");
  const lengths = [0];
  for (let i = 1; i < dense.length; i++) lengths.push(lengths[i - 1] + distance(dense[i], dense[i - 1]));
  const total = lengths[lengths.length - 1];
  if (total <= 0) throw new Error("Fourier stroke has zero length");
  let cursor = 1;
  return Array.from({ length: count }, (_, i) => {
    const target = total * i / (closed ? count : count - 1);
    while (cursor < lengths.length - 1 && lengths[cursor] < target) cursor++;
    const span = Math.max(1e-12, lengths[cursor] - lengths[cursor - 1]);
    const fraction = (target - lengths[cursor - 1]) / span;
    return {
      x: dense[cursor - 1].x + (dense[cursor].x - dense[cursor - 1].x) * fraction,
      y: dense[cursor - 1].y + (dense[cursor].y - dense[cursor - 1].y) * fraction,
    };
  });
};

/** Open paths are mirrored for the DFT; only the outward half is ever drawn. */
export const createStrokeFourier = (path: string, closed: boolean, circleCount: number) => {
  const sourceSamples = sampleCubicStroke(path, closed);
  const periodic = closed
    ? sourceSamples
    : [...sourceSamples, ...sourceSamples.slice(1, -1).reverse()];
  const coefficients = dft(periodic);
  const dc = coefficients.find((coefficient) => coefficient.frequency === 0)!;
  const terms = selectCoefficients(coefficients, circleCount);
  const phaseEnd = closed ? 1 : 0.5;
  const resolution = 512;
  const trail = sampleTrail(terms, dc, resolution).slice(0, resolution * phaseEnd + 1);
  trail[trail.length - 1] = epicycles(terms, dc, phaseEnd)[terms.length - 1].tip;
  return { sourceSamples, terms, dc, phaseEnd, trail, fullPath: toSvgPath(trail) };
};
